// src/screens/SmartLockPage.jsx
import React, { useState, useEffect, useRef, useMemo } from 'react';
import { useParams } from 'react-router-dom';
import {
  Container,
  Center,
  Flex,
  Button,
  TextInput,
  Text,
  Paper,
  Title,
  Loader,
  Space,
  Box,
  Image,
} from '@mantine/core';
import Confetti from 'react-confetti';
import Lottie from 'lottie-react';
import PhoneInput from 'react-phone-number-input';
import { GooglePlayButton, AppStoreButton } from "react-mobile-app-button";

// Lottie JSONs
import wclogoAnimation from '../assets/lottie/wclogo.json';
import stilllogoAnimation from '../assets/lottie/wclogostill.json';
import signalAnimation from '../assets/lottie/signal.json';
import unlockingAnimation from '../assets/lottie/unlocking.json';
import unlockedAnimation from '../assets/lottie/unlocked.json';
import errorAnimation from '../assets/lottie/error.json';
import verifyAnimation from '../assets/lottie/verify.json';

import { CONFIG } from '../atoms/config';
import {
  getSite,
  checkUserAuth,
  confirmVerificationCode,
  unlockAgain,
  activateHiredSession,
  getCurrentBusinessID,
  deactivateHiredSession,
} from '../atoms/network';

import StartHireView from './StartHireView';
import ReportDamageFlow from '../atoms/ReportDamageFlow';

// ─────────────────────────────────────────────────────────────
// Styles
// ─────────────────────────────────────────────────────────────
const S = {
  fullHeightContainer: { minHeight: '100vh' },
  fullScreenContainer: { position: 'relative', minHeight: '100vh', overflow: 'hidden' },
  fullHeightCenter: { height: '100%' },
  logoCircleWrapper: (size = 175) => ({
    position: 'relative',
    width: size,
    height: size,
    marginTop: 110,
    zIndex: 1,
  }),
  logoCircleBg: (size = 175) => ({
    position: 'absolute',
    top: 0,
    left: 0,
    width: size,
    height: size,
    borderRadius: '50%',
    backgroundColor: 'white',
    zIndex: 0,
  }),
  labelLeftAlign: { style: { textAlign: 'left' } },
  paperMax: { maxWidth: 480, marginInline: 'auto', textAlign: 'left' },
  unlockedBox: { textAlign: 'center', zIndex: 1, alignItems: 'center', width: '100%', maxWidth: 520 },
  lottieUnlocked: { width: 150, height: 150 },
  lottieVerify: { width: 200, height: 230, marginTop: 16 },
  lottieError: { width: 200, height: 200 },
  lottieSignal: { opacity: 0.5, width: 600, height: 600 },
  lottieUnlocking: { width: 150, height: 150 },
  bgImage: { position: 'absolute', top: 0, left: 0, zIndex: 0 },
};

// ─────────────────────────────────────────────────────────────
// Helpers
// ─────────────────────────────────────────────────────────────
function truthy(v) {
  return v === true || v === 1 || v === '1' || v === 'true';
}

// Geolocation helpers
function getPositionOnce(opts = {}) {
  return new Promise((resolve, reject) => {
    navigator.geolocation.getCurrentPosition(resolve, reject, opts);
  });
}
function raceOnceWithWatch(opts = {}) {
  return new Promise((resolve, reject) => {
    let settled = false;
    let watchId = null;

    const clear = () => {
      if (watchId != null) navigator.geolocation.clearWatch(watchId);
    };

    try {
      watchId = navigator.geolocation.watchPosition(
        (pos) => {
          if (!settled) {
            settled = true;
            clear();
            resolve(pos);
          }
        },
        // ignore watch errors; the one-shot may still succeed
        () => { },
        opts
      );
    } catch {
      // ignore
    }

    getPositionOnce(opts)
      .then((pos) => {
        if (!settled) {
          settled = true;
          clear();
          resolve(pos);
        }
      })
      .catch((err) => {
        if (!settled) {
          settled = true;
          clear();
          reject(err);
        }
      });
  });
}
async function getBestEffortPosition({
  attempts = 3,
  baseDelayMs = 900,
  preciseOpts = { enableHighAccuracy: true, timeout: 12000, maximumAge: 15000 },
  coarseOpts = { enableHighAccuracy: false, timeout: 12000, maximumAge: 60000 },
} = {}) {
  let lastErr = null;

  // 1) Try a few times with high-accuracy (GPS)
  for (let i = 0; i < attempts; i++) {
    try {
      const pos = await raceOnceWithWatch(preciseOpts);
      return { pos, coarse: false };
    } catch (err) {
      lastErr = err;
      const isTransient =
        err && (err.code === 2 || err.message?.includes('kCLErrorLocationUnknown'));
      if (!isTransient) break;
      const wait = baseDelayMs * Math.pow(1.5, i);
      // eslint-disable-next-line no-await-in-loop
      await new Promise((r) => setTimeout(r, wait));
    }
  }

  try {
    const pos = await raceOnceWithWatch(coarseOpts);
    return { pos, coarse: true };
  } catch (err) {
    try {
      const pos = await getPositionOnce({ enableHighAccuracy: false, timeout: 8000, maximumAge: Infinity });
      return { pos, coarse: true };
    } catch (err2) {
      throw lastErr || err || err2;
    }
  }
}
function getDistanceFromLatLonInM(lat1, lon1, lat2, lon2) {
  const R = 6371e3;
  const toRad = (x) => (x * Math.PI) / 180;
  const dLat = toRad(lat2 - lat1);
  const dLon = toRad(lon2 - lon1);
  const a =
    Math.sin(dLat / 2) ** 2 +
    Math.cos(toRad(lat1)) * Math.cos(toRad(lat2)) * Math.sin(dLon / 2) ** 2;
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  return R * c;
}
function isWithinAllowedRadius({ user, target, accuracy, coarse }) {
  const baseRadius = 250;
  const coarseRadius = 1500;
  const dist = getDistanceFromLatLonInM(user.lat, user.lng, target.lat, target.lng);
  const useRelaxed = coarse || (typeof accuracy === 'number' && accuracy > 800);
  const allowed = useRelaxed ? coarseRadius : baseRadius;
  return { ok: dist <= allowed, dist, allowed, relaxed: useRelaxed };
}

// ─────────────────────────────────────────────────────────────
// Auth flow states
// ─────────────────────────────────────────────────────────────
const AuthFlow = {
  UNKNOWN: 'UNKNOWN',
  NO_LOCK: 'NO_LOCK',
  NO_LOCATION: 'NO_LOCATION',
  INPUT_DETAILS: 'INPUT_DETAILS',
  LOADING: 'LOADING',
  ENTER_OTP: 'ENTER_OTP',
  UNLOCKED: 'UNLOCKED',
  EXPIRED: 'EXPIRED',
  NOT_NEAR_LOCATION: 'NOT_NEAR_LOCATION',
  HIRE_SELECT: 'HIRE_SELECT',
};

// ─────────────────────────────────────────────────────────────
// Small components
// ─────────────────────────────────────────────────────────────
const UnlockedLottie = React.memo(
  function UnlockedLottie() {
    const lottieRef = useRef(null);
    useEffect(() => {
      lottieRef.current?.play?.();
    }, []);
    return (
      <Lottie
        lottieRef={lottieRef}
        animationData={unlockedAnimation}
        loop={false}
        autoplay
        style={S.lottieUnlocked}
      />
    );
  },
  () => true
);

const LogoWithCircle = React.memo(function LogoWithCircle({ showAnimatedLogo }) {
  const size = 175;
  return (
    <Flex justify="center" align="center" style={S.logoCircleWrapper(size)}>
      <Box style={S.logoCircleBg(size)} />
      <Lottie
        animationData={wclogoAnimation}
        // loop={!showAnimatedLogo}
        loop={false}
        autoplay
        style={{ position: 'relative', width: size - 16, height: size - 16, zIndex: 2, paddingRight: 5 }}
      />
    </Flex>
  );
});

const RemainingTimeDisplay = React.memo(function RemainingTimeDisplay({ remainingTime }) {
  return (
    <Text size="lg" mt="sm" ta="center">
      Remaining Time: {remainingTime} seconds
    </Text>
  );
});

const EnterOtpView = React.memo(function EnterOtpView({ onConfirmOTP, verificationCodeErr }) {
  const [verificationCode, setVerificationCode] = useState('');
  const onConfirm = () => onConfirmOTP(verificationCode);

  return (
    <Container size={700} px="md" style={S.fullHeightContainer}>
      <Center style={S.fullHeightCenter}>
        <Flex direction="column" w="100%">
          <Center>
            <Lottie animationData={verifyAnimation} style={S.lottieVerify} loop={false} />
          </Center>
          <Center>
            <Title order={3} align="center">
              Enter Verification Code
            </Title>
          </Center>
          <Space h="md" />
          <TextInput
            label="Verification Code"
            placeholder="123456"
            size="md"
            type="tel"
            value={verificationCode}
            onChange={(e) => setVerificationCode(e.currentTarget.value)}
            labelProps={S.labelLeftAlign}
          />
          {verificationCodeErr && (
            <Text color="red" mt="md">
              {verificationCodeErr}
            </Text>
          )}
          <Space h="md" />
          <Button size="md" fullWidth onClick={onConfirm}>
            Confirm Code
          </Button>
        </Flex>
      </Center>
    </Container>
  );
});

const LoadingView = React.memo(function LoadingView() {
  return (
    <Container fluid px={0} style={S.fullScreenContainer}>
      <Center style={{ ...S.fullHeightCenter, position: 'relative' }}>
        <Flex justify="center" direction="column">
          <Flex
            style={{
              position: 'relative',
              alignItems: 'center',
              justifyContent: 'center',
              width: '100%',
            }}
          >
            <Lottie style={S.lottieSignal} animationData={signalAnimation} loop />
            <Flex
              style={{
                position: 'absolute',
                width: '100%',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <Flex direction="column" align="center" w="100%">
                <Lottie style={S.lottieUnlocking} animationData={unlockingAnimation} loop />
              </Flex>
            </Flex>
          </Flex>
        </Flex>
      </Center>
    </Container>
  );
});

const NoLockView = React.memo(function NoLockView({
  title = 'No App Clip Scanned',
  message = "You haven't scanned a QR code yet. Please scan the QR code on the smart lock to begin your access.",
}) {
  return (
    <Container size={700} px="md" style={S.fullHeightContainer}>
      <Center style={S.fullHeightCenter}>
        <Flex direction="column" align="center" justify="center">
          <Lottie animationData={errorAnimation} style={S.lottieError} loop={false} />
          <Title>{title}</Title>
          <Text align="center" style={{ maxWidth: 300 }}>
            {message}
          </Text>
        </Flex>
      </Center>
    </Container>
  );
});

const NoLocationView = React.memo(function NoLocationView({ onRetry }) {
  return (
    <Container size={700} px="md" style={S.fullHeightContainer}>
      <Center style={S.fullHeightCenter}>
        <Flex direction="column" align="center" justify="center" gap="sm">
          <Lottie animationData={errorAnimation} style={S.lottieError} loop={false} />
          <Title style={{ textAlign: 'center' }}>Location Not Confirmed</Title>
          <Text align="center" style={{ maxWidth: 340 }}>
            We couldn’t get a precise fix. On iPhone, make sure Safari has access to Location
            and “Precise Location” is enabled for this site, then try again.
          </Text>
          <Button mt="sm" onClick={onRetry}>Try again</Button>
        </Flex>
      </Center>
    </Container>
  );
});

const NotNearLocationView = React.memo(function NotNearLocationView() {
  return (
    <Container size={700} px="md" style={S.fullHeightContainer}>
      <Center style={S.fullHeightCenter}>
        <Flex direction="column" align="center" justify="center">
          <Lottie animationData={errorAnimation} style={S.lottieError} loop={false} />
          <Title style={{ textAlign: 'center' }}>Invalid Location</Title>
          <Text align="center" style={{ maxWidth: 300 }}>
            We found that you are not near the location of the item, please try again later.
          </Text>
        </Flex>
      </Center>
    </Container>
  );
});

const ExpiredView = React.memo(function ExpiredView() {
  return (
    <Container size={700} px="md" style={{ height: '100vh', background: 'linear-gradient(white, #f2f2f2)' }}>
      <Center style={S.fullHeightCenter}>
        <Flex direction="column" align="center" justify="center">
          <Lottie animationData={errorAnimation} style={S.lottieError} loop={false} />
          <Title>Access Expired</Title>
          <Text align="center" style={{ maxWidth: 300, marginTop: 8 }}>
            Your temporary access period has ended. If you need access again, scan the QR code or call Council.
          </Text>
        </Flex>
      </Center>
    </Container>
  );
});

const InputDetailsView = React.memo(function InputDetailsView({
  site,
  lock,
  isBanned,
  showAnimatedLogo,
  name,
  setName,
  phoneNumber,
  setPhoneNumber,
  errorMsg,
  onSubmit,
}) {
  const backgroundUrl = useMemo(() => site?.image_remote_url || null, [site?.image_remote_url]);
  const imageHeight = '220px';

  const pricePerMin = useMemo(() => {
    const n = lock?.hire_cost_base;
    if (typeof n !== 'number' || Number.isNaN(n)) return null;
    const f = new Intl.NumberFormat('en-AU', {
      style: 'currency',
      currency: 'AUD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 2,
    });
    return `${f.format(n)} per/min`;
  }, [lock?.hire_cost_base]);

  const maxMins = useMemo(() => {
    const v = lock?.hire_max_length;
    return typeof v === 'number' && !Number.isNaN(v) ? `${v} minutes` : null;
  }, [lock?.hire_max_length]);

  return (
    <Container key="input-details" size={700} px="md" style={{ ...S.fullHeightContainer, position: 'relative', overflow: 'hidden' }}>
      <Flex justify="center">
        {backgroundUrl && (
          <Image src={backgroundUrl} width="100%" height={imageHeight} style={S.bgImage} />
        )}
        <LogoWithCircle showAnimatedLogo={showAnimatedLogo} />
      </Flex>

      <Center style={{ paddingTop: 12, paddingBottom: 100 }}>
        <Box style={{ textAlign: 'center', width: '100%' }}>
          <Title order={3}>Welcome to {site?.title}</Title>
          <Text size="sm" c="dimmed">{lock?.title}</Text>

          {lock?.is_ajax_item && (pricePerMin || maxMins) && (
            <Flex align="center" gap={8} justify="center" mt={6} c="dimmed">
              {pricePerMin && <Text size="xs">{pricePerMin}</Text>}
              {pricePerMin && maxMins && <Text size="xs">•</Text>}
              {maxMins && <Text size="xs">{maxMins}</Text>}
            </Flex>
          )}

          <Space h={28} />

          <Box style={{ maxWidth: '100%', marginInline: 'auto', textAlign: 'left' }}>
            <TextInput
              placeholder="Enter Name (Optional)"
              value={name}
              onChange={(e) => setName(e.currentTarget.value)}
              mb="sm"
              size="md"
              labelProps={S.labelLeftAlign}
            />
            <PhoneInput
              international
              defaultCountry="AU"
              countryCallingCodeEditable={false}
              placeholder="Enter phone number"
              value={phoneNumber}
              onChange={setPhoneNumber}
              inputComponent={TextInput}
              size="md"
              inputProps={{ size: 'md', mb: 'md', labelProps: S.labelLeftAlign }}
            />
            {errorMsg && (
              <Text color="red" size="sm" mb="md">
                {errorMsg}
              </Text>
            )}
            {
              isBanned &&
              <Button
                component="a"
                href="tel:0364726800"
                color="green"
                size="md"
                mt="md"
                fullWidth
              >
                Call Council
              </Button>
            }
            <Button size="md" mt="md" fullWidth onClick={onSubmit}>
              {lock?.is_ajax_item ? 'Next' : 'Unlock Door'}
            </Button>
          </Box>

          <Space h="md" />
          <Text size="xs" style={{ color: '#999' }}>
            By continuing, you agree to our{' '}
            <a href="https://www.autech.com.au/unlockwc-privacy">Privacy Policy</a> and{' '}
            <a href="https://www.autech.com.au/unlockwc-tc">T&Cs</a>.
          </Text>
        </Box>
      </Center>
    </Container>
  );
});

const UnlockedView = React.memo(function UnlockedView({
  isAjaxItem,
  isHireStarted,
  onStartHire,
  startingHire,
  onUnlockAgain,
  unlocking,
  remainingTime,
  hireMaxLength,
  sessionPurchasedTime = 0,
  errorMsg,
  setAuthFlow,
  AuthFlowRef,
  showConfetti,
  setHireMode,
  onReportDamage, // NEW
}) {
  const extendAvailable = useMemo(
    () => Math.max(0, Number(hireMaxLength || 0) - Number(sessionPurchasedTime || 0)),
    [hireMaxLength, sessionPurchasedTime]
  );

  return (
    <Container size={700} px="md" fluid>
      {showConfetti && <Confetti recycle={false} />}
      <Center>
        <Box style={S.unlockedBox}>
          <Flex justify="center" mt="64px">
            <UnlockedLottie />
          </Flex>

          {isAjaxItem ? (
            <Title order={2} mt="xs">Item Hired</Title>
          ) : (
            <Title order={2} mt="xs">Door Unlocked</Title>
          )}

          {(!isAjaxItem || (isAjaxItem && isHireStarted)) && (
            <RemainingTimeDisplay remainingTime={remainingTime} />
          )}

          <Paper shadow="xs" p="md" mt="md" sx={S.paperMax}>
            {isAjaxItem ? (
              <>
                <Text size="sm" mb="sm">
                  1. Start your hire by pressing the green button below.<br />
                  2. Once your hire is started, you can use the hot water for the paid amount of time.<br />
                  3. Keep your phone handy in case you need support.
                </Text>

                {!isHireStarted && (
                  <Button
                    fullWidth
                    onClick={onStartHire}
                    color="green"
                    mt="sm"
                    loading={startingHire}
                    disabled={startingHire}
                  >
                    Start Hire
                  </Button>
                )}

                {isHireStarted && !isAjaxItem && (
                  <Button
                    fullWidth
                    onClick={onUnlockAgain}
                    color="blue"
                    mt="sm"
                    loading={unlocking}
                    disabled={unlocking}
                  >
                    Unlock Again
                  </Button>
                )}

                {extendAvailable > 0 && (
                  <Button
                    variant="light"
                    fullWidth
                    color="orange"
                    mt="sm"
                    onClick={() => {
                      setHireMode('extend');
                      setAuthFlow(AuthFlowRef.HIRE_SELECT);
                    }}
                  >
                    Extend
                  </Button>
                )}

                {errorMsg && <Text c="red" size="sm" mt="sm">{errorMsg}</Text>}

                <Button variant="outline" fullWidth mt="sm" onClick={() => window.open('tel://0364726800')}>
                  Call Council
                </Button>

                <Button variant="outline" color="red" fullWidth mt="sm" onClick={onReportDamage}>
                  Report Damage
                </Button>
              </>
            ) : (
              <>
                <Text size="sm" mb="sm">
                  You now have temporary access to the facility. This access is valid for a limited
                  time. For assistance, call Council.
                </Text>

                <Button
                  fullWidth
                  onClick={onUnlockAgain}
                  color="blue"
                  mt="sm"
                  loading={unlocking}
                  disabled={unlocking}
                >
                  Unlock Again
                </Button>

                {errorMsg && <Text c="red" size="sm" mt="sm">{errorMsg}</Text>}

                <Button variant="outline" fullWidth mt="sm" onClick={() => window.open('tel://0364726800')}>
                  Call Council
                </Button>

                <Button variant="outline" color="red" fullWidth mt="sm" onClick={onReportDamage}>
                  Report Damage
                </Button>
              </>
            )}
          </Paper>
        </Box>
      </Center>
    </Container >
  );
});

const MembershipOnlyView = React.memo(function MembershipOnlyView({
  title = 'Members-only access',
  message = 'This facility can only be accessed by member users. Please download our full app to access this.',
  appleLink = '',
  googleLink = '',
}) {
  const hasApple = typeof appleLink === 'string' && appleLink.trim().length > 0;
  const hasGoogle = typeof googleLink === 'string' && googleLink.trim().length > 0;

  return (
    <Container size={700} px="md" style={S.fullHeightContainer}>
      <Center style={S.fullHeightCenter}>
        <Flex direction="column" align="center" justify="center" gap="sm" w="100%">
          <LogoWithCircle showAnimatedLogo={false} />
          <Title ta="center">{title}</Title>
          <Text ta="center" c="dimmed" style={{ maxWidth: 360 }}>{message}</Text>

          <Flex mt="md" gap="md" wrap="wrap" justify="center" w="100%" style={{ maxWidth: 420 }}>
            {hasApple && (
              <AppStoreButton
                url={appleLink}
                theme={"dark"}
                className={"custom-style"}
                width={'auto'}
              />
            )}

            {hasGoogle && (
              <GooglePlayButton
                url={googleLink}
                theme={"dark"}
                className={"custom-style"}
                width={'auto'}
              />
            )}
          </Flex>

          {/* {(!hasApple && !hasGoogle) && (
            <Text size="sm" mt="sm" ta="center" c="dimmed">
              App store links are unavailable. Please search your app store for <b>Unlock West Coast</b>,
              or contact Council for assistance.
            </Text>
          )}

          <Button variant="outline" mt="md" onClick={() => window.open('tel://0364726800')}>
            Call Council
          </Button> */}
        </Flex>
      </Center>
    </Container>
  );
});


// ─────────────────────────────────────────────────────────────
// Main Page
// ─────────────────────────────────────────────────────────────
export default function SmartLockPage() {
  const { siteID, itemID } = useParams();

  const [authFlow, setAuthFlow] = useState(AuthFlow.UNKNOWN);
  const [name, setName] = useState('');
  const [phoneNumber, setPhoneNumber] = useState('');
  const [errorMsg, setErrorMsg] = useState('');
  const [verificationCodeErr, setVerificationCodeErr] = useState('');
  const [remainingTime, setRemainingTime] = useState(300);
  const [timerId, setTimerId] = useState(null);
  const [showConfetti, setShowConfetti] = useState(false);
  const [showAnimatedLogo, setShowAnimatedLogo] = useState(true);
  const [site, setSite] = useState({});
  const [lock, setLock] = useState({});
  const [invalidLock, setInvalidLock] = useState(false);
  const [isBanned, setIsBanned] = useState(false);

  const [appStoreLink, setAppStoreLink] = useState('');
  const [googlePlayStoreLink, setGooglePlayStoreLink] = useState('');
  const [isMembershipOnly, setIsMembershipOnly] = useState(false);
  // Report Damage
  const [showReportDamage, setShowReportDamage] = useState(false);

  // Hire/session state (AJAX path)
  const [sessionId, setSessionId] = useState(null);
  const [sessionLength, setSessionLength] = useState(0);
  const [isHireStarted, setIsHireStarted] = useState(false);
  const [startingHire, setStartingHire] = useState(false);
  const [unlocking, setUnlocking] = useState(false);
  const [hireMode, setHireMode] = useState('initial');

  useEffect(() => {
    if (showConfetti) {
      const timer = setTimeout(() => setShowConfetti(false), 1800);
      return () => clearTimeout(timer);
    }
  }, [showConfetti]);

  useEffect(() => {
    if (!itemID) {
      setAuthFlow(AuthFlow.NO_LOCK);
      return;
    } else {
      getSiteData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [itemID]);

  useEffect(() => {
    if (Object.values(site).length > 0) {
      requestLocationCheck();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [site]);

  function startCountdown() {
    if (timerId) clearInterval(timerId);
    const id = setInterval(() => {
      setRemainingTime((prev) => {
        if (prev <= 1) {
          clearInterval(id);
          setAuthFlow(AuthFlow.EXPIRED);
          onEndHire();
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
    setTimerId(id);
  }

  useEffect(() => {
    if (authFlow === AuthFlow.UNLOCKED && !lock?.is_ajax_item) {
      startCountdown();
      return () => { if (timerId) clearInterval(timerId); };
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authFlow, lock?.is_ajax_item]);

  useEffect(() => {
    if (authFlow === AuthFlow.UNLOCKED && lock?.is_ajax_item && isHireStarted && !timerId) {
      startCountdown();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authFlow, lock?.is_ajax_item, isHireStarted]);

  useEffect(() => {
    if (authFlow !== AuthFlow.UNLOCKED && timerId) {
      clearInterval(timerId);
      setTimerId(null);
    }
  }, [authFlow, timerId]);

  async function getSiteData() {
    const response = await getSite(itemID);
    if (!response?.data) {
      setAuthFlow(AuthFlow.NO_LOCK);
      setErrorMsg('Could not load venue.');
      setInvalidLock(true);
      return;
    }

    if (response.status) {
      if (response.data.is_membership) {
        // NEW: record links + mark as members-only
        setIsMembershipOnly(true);
        setGooglePlayStoreLink(response.data?.google_link ?? '');
        // handle both possible keys just in case (apple_link or apple link)
        setAppStoreLink(response.data?.apple_link ?? response.data?.apple ?? '');

        // keep your existing behaviour
        setAuthFlow(AuthFlow.NO_LOCK);
        setErrorMsg('This facility can only be accessed by member users, please download our full app to access this');
        setInvalidLock(true);
      } else {
        setSite(response.data.site || {});
        setLock(response.data.item || {});
      }
    } else {
      setAuthFlow(AuthFlow.NO_LOCK);
      setErrorMsg(`${response.message}`);
      setInvalidLock(true);
    }
  }

  async function requestLocationCheck() {
    if (CONFIG.devMode) {
      setAuthFlow(AuthFlow.INPUT_DETAILS);
      return;
    }

    const lat = Number(site?.lat);
    const lng = Number(site?.lng);
    if (!Number.isFinite(lat) || !Number.isFinite(lng)) {
      console.warn('[Location] Missing site lat/lng');
      setAuthFlow(AuthFlow.NO_LOCATION);
      return;
    }

    try {
      if (navigator.permissions?.query) {
        const perm = await navigator.permissions.query({ name: 'geolocation' });
        if (perm.state === 'denied') {
          console.warn('[Location] Permission denied');
          setAuthFlow(AuthFlow.NO_LOCATION);
          return;
        }
      }
    } catch { /* ignore */ }

    try {
      const { pos, coarse } = await getBestEffortPosition();

      const coords = pos.coords || {};
      const user = { lat: coords.latitude, lng: coords.longitude };
      const target = { lat, lng };

      const { ok } = isWithinAllowedRadius({
        user, target, accuracy: coords.accuracy, coarse
      });

      if (ok) {
        setAuthFlow(AuthFlow.INPUT_DETAILS);
      } else {
        setAuthFlow(AuthFlow.NOT_NEAR_LOCATION);
      }
    } catch (err) {
      console.warn('[Location] best-effort failed:', err);
      setAuthFlow(AuthFlow.NO_LOCATION);
    }
  }

  async function handleUnlockClick() {
    if (!phoneNumber) {
      setErrorMsg('Phone number is required.');
      return;
    }
    if (!getCurrentBusinessID()) {
      setErrorMsg('Still loading venue. Please wait a second and try again.');
      return;
    }

    setErrorMsg('');
    setAuthFlow(AuthFlow.LOADING);

    const submittedName = name.trim() === '' ? 'Opt Out' : name;

    try {
      const response = await checkUserAuth(itemID, submittedName, phoneNumber, '');
      const data = response?.data || {};

      if (truthy(data.is_banned)) {
        setAuthFlow(AuthFlow.INPUT_DETAILS);
        setErrorMsg('Your account has been banned by West Coast Council, please contact West Coast Council for more information');
        setIsBanned(true);
        return;
      }

      if (truthy(data.is_hired_by_another)) {
        setAuthFlow(AuthFlow.INPUT_DETAILS);
        setErrorMsg('This facility has been hired by another person. Please try again later.');
        return;
      }

      if (lock?.is_ajax_item && truthy(data.has_session) && data.session) {
        const sess = data.session;
        setSessionId(sess.id);
        setSessionLength(Number(sess.length || 0));
        const remainSec = Number(sess.remaining_seconds ?? sess.remain_seconds ?? 0);
        const started = truthy(sess.is_started) || truthy(sess.started);
        if (started) {
          setIsHireStarted(true);
          setRemainingTime(remainSec);
        } else {
          setIsHireStarted(false);
          setRemainingTime(Number(sess.length || 0) * 60);
        }
        setAuthFlow(AuthFlow.UNLOCKED);
        return;
      }

      if (truthy(data.auth)) {
        if (lock?.is_ajax_item) {
          setHireMode('initial');
          setAuthFlow(AuthFlow.HIRE_SELECT);
        } else {
          setShowConfetti(true);
          setAuthFlow(AuthFlow.UNLOCKED);
        }
      } else {
        setAuthFlow(AuthFlow.ENTER_OTP);
      }
    } catch (err) {
      setAuthFlow(AuthFlow.INPUT_DETAILS);
      setErrorMsg(`Failed to authenticate: ${err.message || err.toString()}`);
    }
  }

  async function handleConfirmOTP(codeEnteredByUser) {
    if (!codeEnteredByUser) {
      setVerificationCodeErr('Verification code is required.');
      setAuthFlow(AuthFlow.ENTER_OTP);
      return;
    }
    setVerificationCodeErr('');
    setAuthFlow(AuthFlow.LOADING);

    try {
      const compareRes = await confirmVerificationCode(itemID, name, phoneNumber, codeEnteredByUser);
      const ok = truthy(compareRes?.data?.auth);

      if (!ok) {
        setVerificationCodeErr('Invalid verification code.');
        setAuthFlow(AuthFlow.ENTER_OTP);
        return;
      }

      setShowConfetti(true);
      if (lock?.is_ajax_item) {
        setHireMode('initial');
        setAuthFlow(AuthFlow.HIRE_SELECT);
      } else {
        setAuthFlow(AuthFlow.UNLOCKED);
      }
    } catch (err) {
      setVerificationCodeErr(`Verification error: ${err.message || err.toString()}`);
      setAuthFlow(AuthFlow.ENTER_OTP);
    }
  }

  function handleSessionReady({ sessionId: sid, minutes, length }) {
    setSessionId(sid);
    setSessionLength(length || minutes || 0);
    setIsHireStarted(false);
    setRemainingTime((length || minutes || 0) * 60);
    setAuthFlow(AuthFlow.UNLOCKED);
  }

  async function onStartHire() {
    if (!sessionId) {
      setErrorMsg('No session available to start.');
      return;
    }
    setStartingHire(true);
    setErrorMsg('');
    try {
      const res = await activateHiredSession({
        itemID,
        phone: phoneNumber,
        sessionId
      });
      const ok = truthy(res?.data?.is_started) || truthy(res?.data?.started);

      if (ok) {
        setIsHireStarted(true);
        if (!timerId) startCountdown();
      } else {
        setErrorMsg('Failed to start hire, please try again.');
      }
    } catch (e) {
      setErrorMsg(e?.message || 'Failed to start hire.');
    } finally {
      setStartingHire(false);
    }
  }

  async function onEndHire() {
    if (!sessionId) {
      setErrorMsg('No session available to start.');
      return;
    }
    try {
      await deactivateHiredSession({ itemID, sessionId });
    } catch (e) {
      setErrorMsg(e?.message || 'Failed to end hire.');
    } finally {
      setStartingHire(false);
    }
  }

  async function onUnlockAgain() {
    setUnlocking(true);
    try {
      const unlockRes = await unlockAgain(itemID, name, phoneNumber);
      if (!unlockRes?.status) {
        setErrorMsg('Could not unlock again. Please try again.');
      }
    } catch (err) {
      setErrorMsg(`Unlock failed: ${err.message || err.toString()}`);
    } finally {
      setUnlocking(false);
    }
  }

  // ─────────────────────────────────────────────────────────────
  // Render
  // ─────────────────────────────────────────────────────────────
  let content;
  switch (authFlow) {
    case AuthFlow.NO_LOCK:
      content = isMembershipOnly ? (
        <MembershipOnlyView
          title="Members-only access"
          message={errorMsg || 'This facility can only be accessed by member users. Please download our full app to access this.'}
          appleLink={appStoreLink}
          googleLink={googlePlayStoreLink}
        />
      ) : (
        <NoLockView
          title={invalidLock ? 'Item Not Found' : 'No App Clip Scanned'}
          message={
            invalidLock
              ? errorMsg
              : "You haven't scanned a QR code yet. Please scan the QR code on the smart lock to begin your access."
          }
        />
      );
      break;
    case AuthFlow.NO_LOCATION:
      content = <NoLocationView onRetry={requestLocationCheck} />;
      break;
    case AuthFlow.EXPIRED:
      content = <ExpiredView />;
      break;
    case AuthFlow.UNKNOWN:
      content = (
        <Container size={700} px="md" style={{ height: '100vh' }}>
          <Center style={S.fullHeightCenter}>
            <Loader />
          </Center>
        </Container>
      );
      break;
    case AuthFlow.LOADING:
      content = <LoadingView />;
      break;
    case AuthFlow.NOT_NEAR_LOCATION:
      content = <NotNearLocationView />;
      break;
    case AuthFlow.INPUT_DETAILS:
      content = (
        <InputDetailsView
          site={site}
          lock={lock}
          isBanned={isBanned}
          showAnimatedLogo={showAnimatedLogo}
          name={name}
          setName={setName}
          phoneNumber={phoneNumber}
          setPhoneNumber={setPhoneNumber}
          errorMsg={errorMsg}
          onSubmit={handleUnlockClick}
        />
      );
      break;
    case AuthFlow.HIRE_SELECT:
      content = (
        <StartHireView
          itemID={itemID}
          userPhoneNumber={phoneNumber}
          maxMinutes={
            hireMode === 'extend'
              ? Math.max(0, Number(lock?.hire_max_length || 0) - Number(sessionLength || 0))
              : Number(lock?.hire_max_length || 0)
          }
          costPerMinute={lock?.hire_cost_base}
          isExtension={hireMode === 'extend'}
          sessionId={sessionId}
          alreadyPurchasedMinutes={sessionLength}
          onSessionReady={handleSessionReady}
          onExtended={({ newTotalMinutes, remainingSeconds }) => {
            setSessionLength(newTotalMinutes);
            setRemainingTime(remainingSeconds);
            setAuthFlow(AuthFlow.UNLOCKED);
          }}
          onBack={() => setAuthFlow(AuthFlow.UNLOCKED)}
        />
      );
      break;
    case AuthFlow.ENTER_OTP:
      content = (
        <EnterOtpView
          onConfirmOTP={handleConfirmOTP}
          verificationCodeErr={verificationCodeErr}
        />
      );
      break;
    case AuthFlow.UNLOCKED:
      content = (
        <UnlockedView
          isAjaxItem={!!lock?.is_ajax_item}
          isHireStarted={isHireStarted}
          onStartHire={onStartHire}
          startingHire={startingHire}
          onUnlockAgain={onUnlockAgain}
          unlocking={unlocking}
          remainingTime={remainingTime}
          hireMaxLength={lock?.hire_max_length}
          sessionPurchasedTime={sessionLength}
          errorMsg={errorMsg}
          setAuthFlow={setAuthFlow}
          AuthFlowRef={AuthFlow}
          showConfetti={showConfetti}
          setHireMode={setHireMode}
          onReportDamage={() => setShowReportDamage(true)} // NEW
        />
      );
      break;
    default:
      content = (
        <Container size={700} px="md">
          <Center style={S.fullHeightCenter}>
            <Text>Unhandled state: {authFlow}</Text>
          </Center>
        </Container>
      );
      break;
  }

  return (
    <>
      {content}

      {/* Full-screen Report Damage flow */}
      <ReportDamageFlow
        opened={showReportDamage}
        onClose={() => setShowReportDamage(false)}
        siteID={siteID}
        itemID={itemID}
        phone={phoneNumber}
        businessID
      />
    </>
  );
}
